import { useState } from "react";
import { NavLink, Outlet, Navigate, Link } from "react-router-dom";
import {
  ChartLineUp,
  Users,
  Scroll,
  ArrowLeft,
  List,
  X,
  ShieldCheck,
  Sidebar as SidebarIcon,
  Crown,
  CurrencyCircleDollar,
  Buildings,
  ChartLine,
  Headset,
  Question,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: ChartLineUp, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/organizacoes", label: "Organizações", icon: Buildings, testid: "admin-nav-organizations" },
  { to: "/admin/usuarios", label: "Usuários", icon: Users, testid: "admin-nav-users" },
  { to: "/admin/analytics", label: "Analytics", icon: ChartLine, testid: "admin-nav-analytics" },
  { to: "/admin/planos", label: "Planos", icon: Crown, testid: "admin-nav-plans" },
  { to: "/admin/financeiro", label: "Financeiro", icon: CurrencyCircleDollar, testid: "admin-nav-finance" },
  { to: "/admin/suporte", label: "Suporte", icon: Headset, testid: "admin-nav-support" },
  { to: "/admin/faq", label: "FAQ", icon: Question, testid: "admin-nav-faq" },
  { to: "/admin/auditoria", label: "Auditoria", icon: Scroll, testid: "admin-nav-audit" },
];

function AdminSidebarContent({ collapsed = false, onNavigate }) {
  return (
    <div className="flex flex-col h-full bg-gado-greenDark text-white">
      <div className={`flex items-center gap-2.5 h-16 border-b border-white/10 ${collapsed ? "justify-center px-2" : "px-5"}`}>
        <span className="h-9 w-9 rounded-xl bg-gado-gold/20 grid place-items-center shrink-0">
          <ShieldCheck size={20} weight="fill" className="text-gado-goldLight" />
        </span>
        {!collapsed && (
          <div className="min-w-0 leading-tight">
            <p className="font-cabinet font-extrabold text-sm truncate">Área Administrativa</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 truncate">GadoPro · Backoffice</p>
          </div>
        )}
      </div>
      <nav className="flex-1 px-2.5 py-4 space-y-1">
        {ADMIN_NAV.map(({ to, label, icon: Icon, end, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            data-testid={testid}
            title={label}
            className={({ isActive }) =>
              `flex items-center gap-3 ${collapsed ? "justify-center px-2" : "px-3.5"} py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-gado-gold text-gado-ink" : "text-white/70 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <Icon size={20} weight="regular" className="shrink-0" />
            {!collapsed && label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3">
        <Link
          to="/app"
          data-testid="admin-back-to-app"
          className={`flex items-center gap-3 ${collapsed ? "justify-center px-2" : "px-3.5"} py-2.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors`}
        >
          <ArrowLeft size={20} className="shrink-0" />
          {!collapsed && "Voltar ao app"}
        </Link>
      </div>
    </div>
  );
}

export default function AdminShell() {
  const { user, loading } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-gado-cream">
        <p className="text-gado-muted text-sm">Carregando…</p>
      </div>
    );
  }
  if (!user || (user.role || "cliente") !== "super_admin") {
    toast.error("Acesso restrito a administradores");
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="min-h-screen bg-gado-cream flex" data-testid="admin-shell">
      {/* Desktop sidebar — colapsável */}
      <aside
        className={`hidden lg:flex ${collapsed ? "w-20" : "w-64"} shrink-0 flex-col fixed inset-y-0 transition-all duration-200`}
      >
        <AdminSidebarContent collapsed={collapsed} />
        <button
          onClick={() => setCollapsed((c) => !c)}
          data-testid="admin-collapse-toggle"
          aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
          className="absolute -right-3 top-5 h-6 w-6 rounded-full bg-gado-greenDark border border-white/20 text-white grid place-items-center shadow-md hover:bg-gado-green"
        >
          <SidebarIcon size={13} />
        </button>
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <aside className="relative w-64 flex flex-col">
            <AdminSidebarContent onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      <div className={`flex-1 ${collapsed ? "lg:ml-20" : "lg:ml-64"} min-w-0 pl-safe pr-safe transition-all duration-200`}>
        {/* Mobile topbar */}
        <div className="lg:hidden sticky top-0 z-40 bg-gado-greenDark text-white pt-safe">
          <div className="h-14 flex items-center justify-between px-4">
            <button
              data-testid="admin-menu-toggle"
              onClick={() => setDrawerOpen(true)}
              className="p-1.5 -ml-1.5 rounded-md hover:bg-white/10"
              aria-label="Abrir menu administrativo"
            >
              {drawerOpen ? <X size={22} /> : <List size={22} />}
            </button>
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} weight="fill" className="text-gado-goldLight" />
              <span className="font-cabinet font-extrabold text-sm">Área Administrativa</span>
            </div>
            <Link to="/app" className="p-1.5 -mr-1.5 rounded-md hover:bg-white/10" aria-label="Voltar ao app">
              <ArrowLeft size={20} />
            </Link>
          </div>
        </div>

        <main className="p-4 md:p-8 max-w-[1200px] mx-auto pb-safe-plus lg:pb-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
